import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon, Icons } from '@ge/components/icon';
import { MiniLoader } from '@ge/components/loader';
import { DraggableTable, DynamicTable } from '@ge/components/table';
import { TooltipCell } from '@ge/components/table/table';
import { Severity } from '@ge/components/severity';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { ManageDefs } from '@ge/models/constants';
import { DateTimeFormats} from '@ge/models/constants';
import { useFilterDefs } from '@ge/shared/hooks';
import { elevations } from '@ge/tokens/elevations';
import { killEventPropagation } from '@ge/shared/util/general';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import {
  AnomalyTaskColumnDefs,
  AnomalyTaskColumns,
  defaultAnomalyTasksCols,
} from '../../models/table-col-defs/anomaly-task-table-cols';
const ChevronIcon = styled(Icon).attrs((props) => ({
  size: 12,
  color: props.theme.table.iconLightColor,
}))`
  margin-top: 2px;
  margin-right: 4px;
`;
const LinkedWrapper = styled.div`
  &:hover .arrow-icon {
    transform: rotate(-90deg);
    scale: (1.2);
    fill: ${(props) => props.theme.table.iconHighlightColor};
  }
`;

const StyledDesc = styled.span`
  margin-left: 6px;
`;
const Load = styled.div`
  top: 400px;
  position: relative;
`;
const AnomalyTaskTable = ({
  isLoading,
  tableType,
  history,
  columns,
  sortAction,
  sortMetric,
  sortDirection,
  onEventSelect,
  draggable,
  scrollable,
  onDrop,
  onFilter,
  filterValues,
  onFilterChange,
}) => {
  const { showCaseDetails } = useContext(EntityDetailsContext);

  const { t, ready } = useTranslation(['monitor.issues'], {
    useSuspense: false,
  });

  const sortedDirection = useCallback(
    (metric) => (metric === sortMetric ? sortDirection : ''),
    [sortMetric, sortDirection],
  );

  const [tableColumns, setTableColumns] = useState(columns);

  const { filterDefs, onChange: onFilterApply } = useFilterDefs({
    columnDefs: AnomalyTaskColumnDefs,
    stateId: ManageDefs.ANOMALY_TASKS_LIST,
  });
  const handleFilterApply = useCallback(
    (_, columnKey, value) => {
      onFilterApply(columnKey, value);
    },
    [onFilterApply],
  );

  const handleFilterChange = useCallback(
    (_, key, value) => onFilterChange({ key, value }),
    [onFilterChange],
  );

  const handleLinkedClick = useCallback(
    (e, id) => {
      killEventPropagation(e);
      showCaseDetails(id);
    },
    [showCaseDetails],
  );

  useEffect(() => {
    onFilter(filterDefs);
  }, [filterDefs, onFilter]);

  useEffect(() => {
    const filteredColumns = [];
    columns.map((c) => {
      const newCol = { id: c.id, cols: [] };
      const cols = c.cols.filter((f) => f.filter === undefined || f.filter === tableType);
      if (cols.length > 0) {
        newCol.cols = cols;
        filteredColumns.push(newCol);
      }
    });
    setTableColumns(filteredColumns);
  }, [columns, tableType, setTableColumns]);

  const cellValueMapFn = useCallback((tasks) => {
    if (!tasks) {
      return {};
    }
    const { priority, description, createDate, closed, id } = tasks;

    return {
      [AnomalyTaskColumns.PRIORITY]: priority,
      [AnomalyTaskColumns.DESCRIPTION]: description,
      [AnomalyTaskColumns.CREATE_DATE]: dayjs(createDate).format(DateTimeFormats.DEFAULT_DATE),
      [AnomalyTaskColumns.CLOSED]: dayjs(closed).format(DateTimeFormats.DEFAULT_DATE),
      [AnomalyTaskColumns.ID]: id,
    };
  }, []);

  const customCellFn = useCallback((columnKey, cellValue) => {
    if (!cellValue) {
      cellValue = '-';
    }
    if (cellValue === null || cellValue === undefined) cellValue = '-';
    switch (columnKey) {
      case AnomalyTaskColumns.PRIORITY:
        return (
          <TooltipCell tooltip={cellValue} zIndex={elevations.P20}>
            <StyledDesc>
              <Severity level={cellValue?.toLowerCase()} />
            </StyledDesc>
          </TooltipCell>
        );
      case AnomalyTaskColumns.DESCRIPTION:
        return (
          <TooltipCell tooltip={cellValue} zIndex={elevations.P20}>
            <StyledDesc>{cellValue}</StyledDesc>
          </TooltipCell>
        );
      case AnomalyTaskColumns.CREATE_DATE:
        return (
          <TooltipCell tooltip={cellValue} zIndex={elevations.P20}>
            <StyledDesc>{cellValue}</StyledDesc>
          </TooltipCell>
        );
      case AnomalyTaskColumns.CLOSED:
        return (
          <TooltipCell tooltip={cellValue} zIndex={elevations.P20}>
            <StyledDesc>{cellValue}</StyledDesc>
          </TooltipCell>
        );
      case AnomalyTaskColumns.ID:
        return (
          <LinkedWrapper onClick={(e) => handleLinkedClick(e, cellValue)}>
            <StyledDesc>
              <ChevronIcon icon={Icons.CHEVRON} rotate={-90} />
            </StyledDesc>
          </LinkedWrapper>
        );

      default:
        return null;
    }
  }, []);

  /**
   * Bootstrap table factories
   */
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: AnomalyTaskColumnDefs,
    cellValueMapFn,
    customCellFn,
    sortAction,
    sortedDirection,
    draggable,
    filterValues,
    onFilterApply: handleFilterApply,
    onFilterChange: handleFilterChange,
    filters: filterDefs,
  });
  if (!columns || columns.length === 0) {
    return null;
  }
  const dynamicProps = {
    scrollable,
    compressed: true,
    transparent: true,
    noTitles: true,
    columns: tableColumns,
    columnGroupFactory,
    columnFactory,
    cellFactory,
    sortAction,
    values: history,
    onValueSelect: onEventSelect,
    rowKeyProperty: 'id',
    dropHandler: draggable ? onDrop : () => null,
  };

  if (!ready || isLoading) {
    return (
      <>
        <Load>
          <MiniLoader />
        </Load>
      </>
    );
  }

  return React.createElement(draggable ? DraggableTable : DynamicTable, dynamicProps);
};

AnomalyTaskTable.propTypes = {
  isLoading: PropTypes.bool,
  tableType: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  history: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  sortAction: PropTypes.func,
  sortMetric: PropTypes.string,
  sortDirection: PropTypes.string,
  onEventSelect: PropTypes.func,
  onDrop: PropTypes.func,
  scrollable: PropTypes.bool,
  draggable: PropTypes.bool,
  filterValues: PropTypes.object,
  onFilter: PropTypes.func,
  onFilterChange: PropTypes.func,
};

AnomalyTaskTable.defaultProps = {
  columns: defaultAnomalyTasksCols,
  sortAction: () => null,
  sortMetric: '',
  sortDirection: '',
  onEventSelect: () => null,
  onDrop: () => null,
  scrollable: false,
  draggable: false,
  filterValues: {},
  onFilter: () => null,
  onFilterChange: () => null,
};

export default AnomalyTaskTable;
